import React, { useEffect, useState } from 'react';
import { ArrowRight, Menu, X, Sparkles, Radio, Gavel, ShoppingBag, ChevronRight } from 'lucide-react';
import BrandLogo from './BrandLogo';

const NAV = [
  { id: 'features', label: 'Features', icon: Sparkles },
  { id: 'how-it-works', label: 'How it works', icon: Radio },
  { id: 'waitlist', label: 'Waitlist', icon: ShoppingBag },
];

const WaitlistHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  // Scroll indicator
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Body scroll lock + escape to close
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const scrollTo = (id) => {
    setOpen(false);
    // small delay so drawer close animation is visible
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled || open
            ? 'bg-black/85 backdrop-blur-md border-b border-white/5'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-16">
          <a href="#" className="flex items-center relative z-10" aria-label="ChainKart home">
            <BrandLogo size={44} />
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => scrollTo(n.id)}
                className="text-sm text-neutral-300 hover:text-white transition-colors"
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:block">
            <button
              onClick={() => scrollTo('waitlist')}
              className="btn-neon inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold"
            >
              Join Waitlist
              <ArrowRight size={14} />
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden relative h-10 w-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white active:scale-95 transition-transform z-10"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            <span className="absolute inset-0 flex items-center justify-center">
              <Menu
                size={18}
                className={`absolute transition-all duration-300 ${
                  open ? 'opacity-0 rotate-90 scale-75' : 'opacity-100 rotate-0 scale-100'
                }`}
              />
              <X
                size={18}
                className={`absolute transition-all duration-300 ${
                  open ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-75'
                }`}
              />
            </span>
          </button>
        </div>
      </header>

      {/* Mobile backdrop */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile drawer (slides down from below header) */}
      <div
        className={`md:hidden fixed top-16 left-0 right-0 z-40 transition-all duration-350 ease-[cubic-bezier(0.2,0.8,0.2,1)] ${
          open ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        <div className="mx-3 rounded-3xl bg-[#0a0a0c]/95 backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] overflow-hidden">
          {/* Status pill */}
          <div className="px-5 pt-5 pb-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#39D353]/10 border border-[#39D353]/25">
              <span className="h-1.5 w-1.5 rounded-full bg-[#39D353] neon-dot" />
              <span className="text-[11px] tracking-[0.15em] uppercase text-[#39D353] font-semibold">
                Waitlist open
              </span>
            </div>
          </div>

          {/* Nav links */}
          <nav className="px-3 pb-2" aria-label="Mobile primary">
            <ul className="flex flex-col">
              {NAV.map((n, i) => {
                const Icon = n.icon;
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => scrollTo(n.id)}
                      className="w-full group flex items-center gap-4 px-3 py-3.5 rounded-2xl hover:bg-white/[0.04] active:bg-white/[0.06] transition-colors text-left"
                      style={{
                        transitionDelay: open ? `${i * 40}ms` : '0ms',
                      }}
                    >
                      <span className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-neutral-300 group-hover:text-[#39D353] group-hover:border-[#39D353]/30 transition-colors">
                        <Icon size={16} />
                      </span>
                      <span className="flex-1 text-[15px] font-medium text-white">{n.label}</span>
                      <ChevronRight
                        size={16}
                        className="text-neutral-500 group-hover:text-white group-hover:translate-x-0.5 transition-all"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* CTA */}
          <div className="px-5 pt-2 pb-5">
            <button
              onClick={() => scrollTo('waitlist')}
              className="btn-neon w-full inline-flex items-center justify-center gap-2 h-12 rounded-full text-[15px] font-semibold"
            >
              Join the Waitlist
              <ArrowRight size={15} />
            </button>
            <div className="mt-3 text-center text-[11px] tracking-[0.15em] uppercase text-neutral-500">
              Launching Q4 2026
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WaitlistHeader;
